import { Check, Copy, ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { careersPageUrl } from '@/modules/companies/careersPageUrl';
import type { CompanyInboundSettingsApi } from '@/modules/companies/types';
import { Button } from '@/modules/ui/components/button/Button';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { SavedIndicator } from '@/modules/ui/components/saved-indicator/SavedIndicator';
import { Switch } from '@/modules/ui/components/switch/Switch';

interface CareersPageSettingProps {
  settings: CompanyInboundSettingsApi;
  /** The app's origin, prefixed to the slug for the public URL. */
  origin: string;
  isSaving: boolean;
  isSaved: boolean;
  error: string | null;
  onChange: (isCareersPageEnabled: boolean) => void;
  onRetry: () => void;
}

const jobsCount = (count: number) => `${count} ${count === 1 ? 'job' : 'jobs'}`;

/**
 * The Careers Page switch on the Workspace page. Turning it on saves at once; turning it off asks first when jobs
 * would stop accepting applications. The URL stays visible while the page is off, because the slug never changes.
 */
export function CareersPageSetting({
  settings,
  origin,
  isSaving,
  isSaved,
  error,
  onChange,
  onRetry,
}: CareersPageSettingProps) {
  const [confirmOffOpen, setConfirmOffOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = settings.careersPageSlug
    ? careersPageUrl(origin, settings.careersPageSlug)
    : null;

  const change = (isCareersPageEnabled: boolean) => {
    if (!isCareersPageEnabled && settings.inboundJobCount > 0) {
      setConfirmOffOpen(true);
      return;
    }
    onChange(isCareersPageEnabled);
  };

  const copy = () => {
    if (!url) return;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="divide-border flex flex-col divide-y border-y">
      <div className="flex items-start justify-between gap-4 py-4">
        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor="careers-page" className="text-[13px] font-medium">
            Careers Page
          </label>
          <p className="text-muted-foreground text-[13px]">
            {settings.isCareersPageEnabled
              ? `A public page listing your jobs that accept applications. ${jobsCount(settings.inboundJobCount)} listed now. New jobs accept applications by default; existing jobs stay as they are until you turn them on.`
              : 'A public page where people can apply to your jobs. New jobs accept applications by default once it is on; existing jobs stay as they are until you turn them on.'}
          </p>
          {error && (
            <div className="flex flex-wrap items-center gap-2">
              <InlineError>{error}</InlineError>
              <Button onClick={onRetry} size="sm" variant="outline">
                Try again
              </Button>
            </div>
          )}
        </div>
        <div className="relative mt-0.5 flex shrink-0 items-center">
          {/* Out of flow: "Saved" appearing must not narrow and rewrap the description. */}
          <SavedIndicator
            className="absolute top-0.5 right-full mr-3"
            isSaved={isSaved}
          />
          <Switch
            id="careers-page"
            checked={settings.isCareersPageEnabled}
            disabled={isSaving}
            onCheckedChange={change}
          />
        </div>
      </div>

      {url && (
        <div className="flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono text-[13px]">{url}</span>
            {!settings.isCareersPageEnabled && (
              <span className="shrink-0 rounded-full border px-2 py-0.5 text-xs text-neutral-600">
                Offline
              </span>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button onClick={copy} size="sm" variant="outline">
              {copied ? (
                <Check className="size-3.5" />
              ) : (
                <Copy className="size-3.5 text-neutral-500" />
              )}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            {settings.isCareersPageEnabled && (
              <Button asChild size="sm" variant="outline">
                <a href={url} rel="noopener noreferrer" target="_blank">
                  <ExternalLink className="size-3.5 text-neutral-500" />
                  Open
                </a>
              </Button>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOffOpen}
        onOpenChange={setConfirmOffOpen}
        title="Turn off your Careers Page?"
        description={`${jobsCount(settings.inboundJobCount)} will stop accepting applications. Their settings are kept and come back when you turn the Careers Page on again. Applications you already received keep progressing.`}
        confirmText="Turn off"
        confirmingText="Turning off…"
        confirming={isSaving}
        onConfirm={() => {
          onChange(false);
          setConfirmOffOpen(false);
        }}
      />
    </div>
  );
}
